import { AxiosResponse } from 'axios';
import { call, fork, put, takeEvery } from 'redux-saga/effects';

// Domain
import { validateDataMainBoardParams } from '../domain/dataMainBoard/validation';
import {
  DataDepreciationSearchParams,
  DataMainBoardResponse,
  DataMainBoardSearchParams,
} from '../domain/depreciation/types';

// Redux
import {
  DataMainBoardActionCreators,
  DataMainBoardActionTypes,
  FetchDataMainBoardAction,
  dataMainBoardParamsSelector,
  dataMainBoardTableDataSelector,
} from '../redux/server/dataMainBoard';
import { settingsOptionsDepreciationFieldsMainBoardSelector } from '../redux/server/settingsOptionsDepreciation';
import { depreciationSearchParamsSelector } from '../redux/ui/depreciationSetting';
import {
  MainBoardSettingActionCreators,
  MainBoardSettingActionTypes,
  SearchMainBoardAction,
  SearchMainBoardFieldAction,
  mainBoardColumnsOrderSelector,
  mainBoardSettingSelector,
} from '../redux/ui/mainBoardSetting';
// Utils
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { buildColumnsOrder } from '../utils/fieldsOrder';
import { DEPRECIATION_MAINBOARD_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER } from '../utils/tableHeaderSubMenuMultiFilter';
import { handleErrorSaga } from './errorSaga';

/**
 * マルチフィルター用の選択肢オブジェクトを作成
 * @param columns カラム配列
 * @param rows データ行配列
 * @param targetColumnCodes マルチフィルター対象のカラムコード配列
 * @returns 選択肢オブジェクト
 */
function createMultiFilterOptions(
  columns: DataMainBoardResponse['data']['columns'],
  rows: DataMainBoardResponse['data']['rows'],
  targetColumnCodes: string[]
): Record<string, string[]> {
  const multiFilterOptions: Record<string, string[]> = {};

  for (const code of targetColumnCodes) {
    const idx = columns.findIndex((c) => c.code === code);

    if (idx >= 0) {
      // 選択肢オブジェクトを作成
      const values = rows
        .map((row) => row.data.at(idx)?.value)
        .filter((v): v is string => typeof v === 'string');

      const uniqueValues = Array.from(new Set(values));
      multiFilterOptions[code] = uniqueValues;
    } else {
      // カラムが存在しない場合は空配列を設定
      multiFilterOptions[code] = [];
    }
  }

  return multiFilterOptions;
}

/**
 * 指定した検索条件で基板番号別データをAPIから取得する
 * @param api API
 * @param action FetchDataMainBoardAction
 */
export function* fetchDataMainBoardSagas(
  api: Api,
  action: FetchDataMainBoardAction
) {
  try {
    // ローディング開始
    yield put(DataMainBoardActionCreators.fetchDataMainBoardRequestAction());

    // バリデーションチェック
    validateDataMainBoardParams(action.payload.params);

    // 基板番号別データ取得APIをコール
    const response: AxiosResponse<DataMainBoardResponse> = yield call(
      api.get,
      'data/depreciation/mainboard',
      buildConfig(action.payload.params)
    );
    const dataMainBoard = response.data;

    // 成功時、データをReduxに保存
    yield put(
      DataMainBoardActionCreators.fetchDataMainBoardSuccessAction(dataMainBoard)
    );

    // APIレスポンスのsetting（fields、sort、order）をUI側のsearchParamsに保存
    // 目的: 以降のリクエストでUI側の設定を優先し、ユーザーの操作設定を保持する
    // これにより、FE側のロジックが一貫して動作し、BEとFEの処理が混在する問題を解消する
    const { setting } = dataMainBoard;
    const { fields, sort, order } = setting;
    yield put(
      MainBoardSettingActionCreators.setMainBoardSearchParamsAction(
        fields,
        sort,
        order
      )
    );

    // マルチフィルター用の選択肢オブジェクトを作成
    const multiFilterOptions = createMultiFilterOptions(
      dataMainBoard.data.columns,
      dataMainBoard.data.rows,
      DEPRECIATION_MAINBOARD_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER
    );

    // マルチフィルターの選択肢をReduxの状態に保存
    yield put(
      MainBoardSettingActionCreators.selectMainBoardTableHeaderSubMenuMultiFilterSelectOptionsAction(
        multiFilterOptions
      )
    );
  } catch (error: unknown) {
    // 失敗時、状態をリセット
    yield put(DataMainBoardActionCreators.renewDataMainBoardAction());
    yield fork(handleErrorSaga, error);
  }
}

/**
 * FETCH_DATA_MAIN_BOARDをDispatch時の処理
 * @param api API
 */
function* handleFetchDataMainBoardSaga(api: Api) {
  yield takeEvery(
    DataMainBoardActionTypes.FETCH_DATA_MAIN_BOARD,
    fetchDataMainBoardSagas,
    api
  );
}

/**
 * メインテーブル（機種別償却）の検索条件を元に基板番号別データの検索条件を生成する
 * @param kiCode 機種コード
 * @param depreciationSearchParams 機種別償却の検索条件
 * @param mainBoardSearchParams 基板番号別データの検索条件
 * @returns 生成された検索条件
 */
function generateMainBoardSearchParams(
  kiCode: string,
  depreciationSearchParams: DataDepreciationSearchParams | undefined,
  mainBoardSearchParams: DataMainBoardSearchParams
): DataMainBoardSearchParams {
  return {
    // UI側で設定されたテーブル表示設定（fields、sort、order）を保持し、検索条件（日付、機種など）は親テーブルから引き継ぐ
    fields: mainBoardSearchParams.fields,
    sort: mainBoardSearchParams.sort,
    order: mainBoardSearchParams.order,

    // 機種は毎回指定する
    kiList: [kiCode],

    // メインテーブルから継承
    startDate: depreciationSearchParams?.startDate,
    endDate: depreciationSearchParams?.endDate,
    daysAfterIntroduction: depreciationSearchParams?.daysAfterIntroduction,
  };
}

/**
 * 選択した機種で基板番号別データを再取得
 */
function* searchDataMainBoard(action: SearchMainBoardAction) {
  const { kiCode } = action.payload;

  // メインテーブル（機種別償却）の検索条件を取得
  const depreciationSearchParams = yield* customSelect(
    depreciationSearchParamsSelector
  );

  // 基板番号別データの前回の検索条件を取得
  const prevMainBoardApiSearchCondition = yield* customSelect(
    dataMainBoardParamsSelector
  );

  // UI設定を取得
  const uiSetting = yield* customSelect(mainBoardSettingSelector);
  const uiSearchParams = uiSetting?.searchParams;

  // UI設定を優先し、なければ前回のAPI検索条件を使用
  const fieldsToUse =
    uiSearchParams?.fields ?? prevMainBoardApiSearchCondition?.fields;
  const sortToUse =
    uiSearchParams?.sort ?? prevMainBoardApiSearchCondition?.sort;
  const orderToUse =
    uiSearchParams?.order ?? prevMainBoardApiSearchCondition?.order;

  // 検索パラメータを生成
  const generatedSearchParams = generateMainBoardSearchParams(
    kiCode,
    depreciationSearchParams,
    {
      ...(prevMainBoardApiSearchCondition ?? {}),
      fields: fieldsToUse,
      sort: sortToUse,
      order: orderToUse,
    }
  );

  // API取得
  yield put(
    DataMainBoardActionCreators.fetchDataMainBoardAction(generatedSearchParams)
  );
}

/**
 * SEARCH_MAIN_BOARDをDispatch時の処理
 */
function* handleSearchDataMainBoardSaga() {
  yield takeEvery(
    MainBoardSettingActionTypes.SEARCH_MAIN_BOARD,
    searchDataMainBoard
  );
}

/**
 * 選択した表示項目で基板番号別データを再取得
 */
function* searchDataMainBoardFieldSagas(action: SearchMainBoardFieldAction) {
  // 現在の検索条件（server側）
  const searchCondition = yield* customSelect(dataMainBoardParamsSelector);

  // UI設定を取得
  const uiSetting = yield* customSelect(mainBoardSettingSelector);
  const uiSearchParams = uiSetting?.searchParams;

  // UI設定を優先し、なければserver側の設定を使用
  const sortToUse = uiSearchParams?.sort ?? searchCondition?.sort;
  const orderToUse = uiSearchParams?.order ?? searchCondition?.order;

  const fieldCodes = action.payload.fields;
  const fieldsOrder = action.payload.fieldsOrder;
  if (fieldsOrder != null && fieldsOrder.length > 0) {
    /** `DepreciationMainBoardTableHeadRow` / `DepreciationMainBoardTableRow` の sticky 列数と一致 */
    const mainBoardStickyColumnCount = 3;

    const persistedColumnsOrder =
      (yield* customSelect(mainBoardColumnsOrderSelector)) ?? [];
    const tableData = yield* customSelect(dataMainBoardTableDataSelector);
    const apiColumnCodes = tableData.columns
      .map((column) => column.code)
      .filter((code): code is string => code != null);

    /**
     * Why: Redux の `columnsOrder` は DnD 前は未設定のまま。空のまま `buildColumnsOrder` に渡すと
     * 先頭固定列用の prefix が取れず、基板番号・購入店舗・現設置店舗が結果から欠落する。
     * 機種別商圏の貸玉（`changeKiSyokenKasidamaFieldsSagas`）などと同様、未設定時は API 応答の列順へフォールバックする。
     */
    const columnsOrderForPattern =
      persistedColumnsOrder.length >= mainBoardStickyColumnCount
        ? persistedColumnsOrder
        : apiColumnCodes;

    const mainBoardFieldOptions = yield* customSelect(
      settingsOptionsDepreciationFieldsMainBoardSelector
    );
    if (
      mainBoardFieldOptions != null &&
      columnsOrderForPattern.length >= mainBoardStickyColumnCount
    ) {
      const resultOrder = buildColumnsOrder(
        fieldsOrder,
        fieldCodes,
        columnsOrderForPattern,
        mainBoardFieldOptions,
        { patternFixedLeftCount: mainBoardStickyColumnCount }
      );
      yield put(
        MainBoardSettingActionCreators.selectMainBoardColumnsOrderAction(
          resultOrder
        )
      );
    }
  }

  // 変更後の表示項目で再取得
  yield put(
    DataMainBoardActionCreators.fetchDataMainBoardAction({
      ...(searchCondition ?? {}),
      fields: fieldCodes,
      sort: sortToUse,
      order: orderToUse,
    })
  );
}

/**
 * テーブル固有の検索条件変更検知時の処理
 */
function* handleTableSearchSaga() {
  // 表示項目変更時
  yield takeEvery(
    MainBoardSettingActionTypes.SEARCH_MAIN_BOARD_FIELD,
    searchDataMainBoardFieldSagas
  );
}

/**
 * 基板番号別データに関するタスクを実行する
 * @param context APIに関するデータ
 */
export function* dataMainBoardSagas(context: { api: Api }) {
  yield fork(handleFetchDataMainBoardSaga, context.api);
  yield fork(handleSearchDataMainBoardSaga);
  yield fork(handleTableSearchSaga);
}
